import React, { useEffect } from "react";
import { Box, Grid } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import CustomTable from "../components/Common/CustomTable";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import ConfirmDialog from "../components/Common/ConfirmDialog";
import DeviceFilter from "../components/Device/DeviceFilter";
import DeviceMenu from "../components/Device/DeviceMenu";
import DeviceUpdate from "../components/Device/DeviceUpdate";
import DeviceAvatar from "../components/Device/DeviceAvatar";
import { useDeviceData } from "./hooks/useDeviceData";
import { useDeviceMenu } from "./hooks/useDeviceMenu";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import { MoreVert } from "@mui/icons-material";
import { DeviceDto } from "../types";

const Device = () => {
  const { showError, snackbarProps } = useSnackbar();
  const [searchParams] = useSearchParams();
  const deviceTypeId = searchParams.get("deviceTypeId")
    ? Number(searchParams.get("deviceTypeId"))
    : undefined;

  const {
    data,
    totalCount,
    page,
    rowPerPage,
    loading,
    searchText,
    setPage,
    getDataWithPagination,
    fetchParserConfig,
    debouncedSearch,
    handleChangePage,
    handleRowsPerPageChange,
    handleSearchChange,
  } = useDeviceData(deviceTypeId);

  const {
    anchorEl,
    selectedRow,
    handleMenuClick,
    handleCloseMenu,
    handleView,
    isUpdateModalOpen,
    handleCloseUpdateModal,
    isDeleteConfirmOpen,
    deleteLoading,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
  } = useDeviceMenu();

  const columns = [
    {
      id: "avatar",
      label: "",
      render: (row: DeviceDto) => (
        <DeviceAvatar deviceTypeId={row.deviceTypeId} iconId={row.iconId ?? 0} size={32} />
      ),
    },
    { id: "deviceName", label: "Cihaz Adı" },
    { id: "description", label: "Açıklama" },
    { id: "parserConfigName", label: "Parser Adı" },
    { id: "serialNumber", label: "Seri No" },
    { id: "deviceTypeName", label: "Cihaz Tipi" },
    {
      id: "menuAction",
      label: "",
      render: (row: DeviceDto) => (
        <MoreVert
          style={{ cursor: "pointer" }}
          onClick={(event: React.MouseEvent<SVGSVGElement>) =>
            handleMenuClick(event, row)
          }
        />
      ),
    },
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [deviceTypeId]);

  useEffect(() => {
    debouncedSearch(page, rowPerPage, searchText);
    return () => debouncedSearch.cancel();
  }, [page, rowPerPage, searchText, debouncedSearch]);

  const loadInitialData = async () => {
    try {
      await fetchParserConfig();
    } catch (error) {
      showError(error as Error);
    }
  };

  const loadDevices = async () => {
    try {
      await getDataWithPagination(page, rowPerPage, searchText);
    } catch (error) {
      showError(error as Error);
    }
  };

  return (
    <>
      <div className="panel-container">
        <Grid
          container
          direction={"column"}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid size={12}>
            <Box
              sx={{
                backgroundColor: "#ffffff",
                padding: "15px",
                alignItems: "center",
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                marginBottom: "20px",
              }}
            >
              <DeviceFilter
                searchText={searchText}
                onSearchChange={handleSearchChange}
                setPage={setPage}
                loadDevices={loadDevices}
              />
            </Box>
          </Grid>
          <Grid
            size={12}
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <CustomTable
              columns={columns}
              data={[...data]}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleRowsPerPageChange}
              onRowClick={() => {}}
              collapsible={false}
              selectable={false}
              sortable={true}
              statusFunction={() => {}}
              showActions={false}
              loading={loading}
            />
          </Grid>
        </Grid>

        <DeviceMenu
          anchorEl={anchorEl}
          onClose={handleCloseMenu}
          onView={handleView}
          onDelete={handleDeleteClick}
          device={selectedRow}
        />
        <DeviceUpdate
          open={isUpdateModalOpen}
          onClose={handleCloseUpdateModal}
          device={selectedRow}
          onSuccess={loadDevices}
        />
        <ConfirmDialog
          open={isDeleteConfirmOpen}
          onClose={handleDeleteCancel}
          onConfirm={() => handleDeleteConfirm(loadDevices, showError)}
          loadingStatus={deleteLoading}
          message="Bu cihazı silmek istediğinize emin misiniz?"
        />
      </div>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default Device;
